import React from 'react';
import { 
  Network, 
  Shield, 
  FileText, 
  Users, 
  Database, 
  BarChart, 
  Brain, 
  Activity 
} from 'lucide-react';

const TechnologiesSection = () => {
  const technologies = [
    {
      icon: <Network className="w-5 h-5 sm:w-5 sm:h-5" />,
      title: "Infor ION",
      description: "Enterprise integration and workflow automation for seamless system connectivity.",
      gradient: "linear-gradient(135deg, #3B82F6 0%, #1E40AF 100%)"
    },
    {
      icon: <Shield className="w-5 h-5 sm:w-5 sm:h-5" />,
      title: "Infor API Gateway",
      description: "Secure API management for safe and scalable application integration.",
      gradient: "linear-gradient(135deg, #10B981 0%, #059669 100%)"
    },
    {
      icon: <FileText className="w-5 h-5 sm:w-5 sm:h-5" />,
      title: "Infor Document Management (IDM)",
      description: "Centralized document management for efficient storage, indexing, and retrieval.",
      gradient: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)"
    },
    {
      icon: <Users className="w-5 h-5 sm:w-5 sm:h-5" />,
      title: "Infor Ming.le",
      description: "Collaboration and digital workspace for enhanced team communication and productivity.",
      gradient: "linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)"
    },
    {
      icon: <Database className="w-5 h-5 sm:w-5 sm:h-5" />,
      title: "Infor Data Lake",
      description: "Centralized data storage for structured and unstructured data across the enterprise.",
      gradient: "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)"
    },
    {
      icon: <BarChart className="w-5 h-5 sm:w-5 sm:h-5" />,
      title: "Infor Birst Analytics",
      description: "Business intelligence and reporting with interactive dashboards and visualizations.",
      gradient: "linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)"
    },
    {
      icon: <Brain className="w-5 h-5 sm:w-5 sm:h-5" />,
      title: "Infor Coleman AI",
      description: "AI-driven insights and automation for predictive analytics and intelligent recommendations.",
      gradient: "linear-gradient(135deg, #EC4899 0%, #BE185D 100%)"
    },
    {
      icon: <Activity className="w-5 h-5 sm:w-5 sm:h-5" />,
      title: "Infor Process Intelligence",
      description: "Process monitoring and optimization to identify bottlenecks and improve efficiency.",
      gradient: "linear-gradient(135deg, #F97316 0%, #EA580C 100%)"
    }
  ];

  return (
    <section className="relative overflow-hidden bg-white py-10 sm:py-12 lg:py-12 xl:py-12">
      <div className="max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header Section */}
        <div className="text-center mb-6 sm:mb-6 lg:mb-8 xl:mb-8">
          <h2 className="text-2xl font-bold tracking-tight sm:text-3xl md:text-4xl"
            style={{ color: '#1A2634' }}> Key Infor OS Technologies
          </h2>
          
          <p className="text-sm sm:text-base lg:text-lg xl:text-lg max-w-3xl lg:max-w-4xl mx-auto leading-relaxed text-slate-600 px-4 sm:px-0">
            Explore the comprehensive suite of Infor OS technologies that power digital transformation and drive business innovation.
          </p>
        </div>

        {/* Technologies Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-5">
          {technologies.map((tech, index) => (
            <div 
              key={index} 
              className="group relative bg-white/80 backdrop-blur-sm rounded-xl sm:rounded-2xl p-3 sm:p-3 lg:p-4 transition-all duration-500 overflow-hidden shadow-sm hover:shadow-md"
            >
              {/* Hover background effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-orange-50/50 to-slate-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              {/* Content */}
              <div className="relative z-10">
                {/* Icon */}
                <div 
                  className="w-9 h-9 sm:w-10 sm:h-10 lg:w-11 lg:h-11 rounded-lg sm:rounded-xl mb-3 sm:mb-3 lg:mb-4 flex items-center justify-center text-white transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-md"
                  style={{ background: tech.gradient }}
                >
                  {tech.icon}
                </div>

                {/* Title */}
                <h3 className="text-sm sm:text-base lg:text-lg font-bold mb-1.5 sm:mb-2 group-hover:text-orange-600 transition-colors duration-300 text-slate-800 leading-tight">
                  {tech.title}
                </h3>

                {/* Description */}
                <p className="text-xs sm:text-sm lg:text-base leading-relaxed text-slate-600 mb-2">
                  {tech.description}
                </p>

                {/* Corner decoration - hidden on mobile */}
                <div className="absolute top-3 sm:top-4 right-3 sm:right-4 w-6 h-6 sm:w-8 sm:h-8 lg:w-10 lg:h-10 rounded-full bg-gradient-to-br from-orange-100/50 to-slate-100/50 opacity-0 group-hover:opacity-100 transition-all duration-500 transform group-hover:scale-110 hidden sm:block"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 h-16 sm:h-24 bg-gradient-to-t from-white/20 to-transparent pointer-events-none"></div>
    </section>
  );
};

export default TechnologiesSection;